/**
 * Marketing runtime configuration.
 *
 * `import.meta.env` is read defensively because this module is also evaluated by
 * the SSR build under Node, where a missing variable must fall back rather than
 * throw during prerender.
 */
const meta = (typeof import.meta !== 'undefined' ? import.meta.env : {}) as Partial<ImportMetaEnv>
/** Same object, read as strings for the `VITE_*` variables. */
const env = meta as unknown as Record<string, string | undefined>

/**
 * PROTOTYPE MODE — the auth bypass.
 *
 * MOD-00's auth screens hand off to Keycloak, which is not running locally, so
 * every sign-in path dead-ends at a redirect to `localhost:8080`. With this on,
 * `src/auth/mockAuth.ts` answers instead: any email and any password sign in, the
 * domain check is served from `mock_data/auth-mock.json`, and the browser is sent
 * to the console — which has its own prototype mode and admits the visitor.
 *
 * WHAT IS NOT RELAXED. This app never held a token to begin with (SEC-FR-001 puts
 * the session in the console), and the mock adds none: no token, no cookie, no web
 * storage write. The ESLint ban on `localStorage`/`sessionStorage` still applies to
 * every file here. What is skipped is a round-trip to an absent identity provider.
 *
 * THE DEFAULTS ARE ASYMMETRIC, matching apps/console/src/app/config.ts so the two
 * apps are never in disagreement about whether the prototype is on:
 *   · dev   → ON unless `VITE_MOCK_API=false`
 *   · prod  → OFF unless `VITE_MOCK_API=true`, so a production build cannot ship an
 *             auth bypass by forgetting a flag
 *   · test  → OFF unless `VITE_MOCK_API=true`. This one is not symmetry for its own
 *             sake: the existing MOD-00 specs assert the *real* contract — that
 *             `ana@gmail.com` is rejected, that the MSW domain-check handler returns
 *             "BrandCo India". Defaulting the bypass on under Vitest would make those
 *             specs pass against the mock instead, which is how a bypass quietly
 *             deletes the coverage of the thing it bypasses.
 */
const mockApiFlag = env.VITE_MOCK_API
export const isPrototypeMode =
  mockApiFlag === 'true'
    ? true
    : mockApiFlag === 'false' || meta.MODE === 'test'
      ? false
      : meta.DEV === true

export const config = {
  apiBaseUrl: env.VITE_API_BASE_URL ?? '',
  prototypeMode: isPrototypeMode,
  consoleUrl: env.VITE_CONSOLE_URL ?? 'http://localhost:5173',
  siteUrl: env.VITE_SITE_URL ?? 'https://insightai.example',
  keycloak: {
    authorizationEndpoint:
      env.VITE_KEYCLOAK_AUTH_URL ??
      'http://localhost:8080/realms/insightai/protocol/openid-connect/auth',
    clientId: env.VITE_KEYCLOAK_CLIENT_ID ?? 'insightai-console',
  },
} as const

/**
 * PKCE redirects land in the console, which owns the session (SEC-FR-001). The
 * marketing app only starts the flow.
 */
export const consoleCallbackUri = `${config.consoleUrl}/auth/callback`
